import { useEffect, useState } from 'react';
import type {
  ImportPreview,
  ImportStatusView,
  ImportVatBody,
  OutletRef,
  VatMode,
} from '@kassa/shared';
import { api, ApiError } from '../api';
import { SALE_TYPE_LABEL, UNIT_LABEL, formatMoney, formatQty } from '../format';

interface Props {
  defaultOutletId: string | null;
  onBack: () => void;
}

const VAT_CHOICES: { mode: VatMode; label: string }[] = [
  { mode: 'vat18', label: 'ƏDV-li (18%)' },
  { mode: 'exempt', label: 'ƏDV-siz (azad)' },
];

// Excel idxalı: yükləmə → baxış (problemlər, ƏDV) → anbar → təsdiq → faiz → nəticə / geri qaytarma.
export function Import({ defaultOutletId, onBack }: Props) {
  const [outlets, setOutlets] = useState<OutletRef[]>([]);
  const [outletId, setOutletId] = useState<string | null>(defaultOutletId);
  const [warehouses, setWarehouses] = useState<{ id: string; name: string }[]>([]);
  const [warehouseId, setWarehouseId] = useState<string>('');
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [status, setStatus] = useState<ImportStatusView | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.outlets().then(setOutlets).catch(() => setOutlets([]));
  }, []);

  useEffect(() => {
    if (!outletId) return;
    api
      .importWarehouses(outletId)
      .then((list) => {
        setWarehouses(list);
        setWarehouseId(list[0]?.id ?? '');
      })
      .catch(() => setWarehouses([]));
  }, [outletId]);

  // Commit işləyəndə vəziyyəti hər saniyə yoxla
  useEffect(() => {
    if (!preview || !status || (status.status !== 'queued' && status.status !== 'running')) return;
    const timer = window.setInterval(() => {
      api
        .importStatus(preview.id)
        .then(setStatus)
        .catch(() => undefined);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [preview, status]);

  async function run<T>(fn: () => Promise<T>): Promise<T | undefined> {
    setBusy(true);
    setError(null);
    try {
      return await fn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Şəbəkə xətası');
      return undefined;
    } finally {
      setBusy(false);
    }
  }

  async function onFile(file: File | undefined) {
    if (!file) return;
    const p = await run(() => api.importPreview(file));
    if (p) {
      setPreview(p);
      setStatus(null);
    }
  }

  async function applyVat(mode: VatMode) {
    if (!preview) return;
    const body: ImportVatBody = { rows: preview.vatUnsetRows, mode };
    const p = await run(() => api.importVat(preview.id, body));
    if (p) setPreview(p);
  }

  async function commit() {
    if (!preview || !outletId || !warehouseId) return;
    const s = await run(() => api.importCommit(preview.id, { outletId, warehouseId }));
    if (s) setStatus(s);
  }

  async function reverse() {
    if (!preview) return;
    const s = await run(() => api.importReverse(preview.id));
    if (s) setStatus(s);
  }

  const running = status !== null && (status.status === 'queued' || status.status === 'running');
  const canCommit =
    preview !== null &&
    preview.readyTotal > 0 &&
    preview.vatUnsetRows.length === 0 &&
    warehouseId !== '' &&
    !busy &&
    status === null;

  return (
    <div className="shell">
      <header className="topbar">
        <span className="brand">Excel idxalı</span>
        <span className="topbar-spacer" />
        <button type="button" className="link-btn back-home" onClick={onBack}>
          Geri
        </button>
      </header>

      <main className="content">
        <section className="card card-wide">
          <h2>Excel idxalı</h2>
          <p className="muted small">Məhsul siyahısını Excel faylı (.xlsx, maks. 10 MB) ilə yükləyin.</p>

          {!status && (
            <label className="field">
              <span>Fayl</span>
              <input
                type="file"
                accept=".xlsx"
                disabled={busy}
                onChange={(e) => void onFile(e.target.files?.[0])}
              />
            </label>
          )}

          {error && <p className="bad">{error}</p>}
          {busy && <p className="muted">Gözləyin…</p>}
        </section>

        {preview && (
          <section className="card card-wide">
            <h2>Baxış: {preview.fileName}</h2>
            <ul className="checks">
              <li>
                <span>Məlumat sətirləri</span>
                <span>{preview.stats.dataRows}</span>
              </li>
              <li>
                <span>İdxala hazır</span>
                <span className="ok">{preview.stats.ready}</span>
              </li>
              <li>
                <span>Rədd olunmuş</span>
                <span className={preview.stats.rejected > 0 ? 'bad' : ''}>{preview.stats.rejected}</span>
              </li>
              <li>
                <span>Xəbərdarlıq</span>
                <span>{preview.stats.warnings}</span>
              </li>
              <li>
                <span>Avtomatik daxili barkod / PLU</span>
                <span>
                  {preview.stats.generatedBarcodes} / {preview.stats.generatedPlu}
                </span>
              </li>
            </ul>

            {preview.issues.length > 0 && (
              <>
                <h3>Problemlər {preview.issuesTotal > preview.issues.length && `(ilk ${preview.issues.length})`}</h3>
                <ul className="plain">
                  {preview.issues.map((i, idx) => (
                    <li key={idx} className={i.severity === 'error' ? 'bad' : 'muted'}>
                      Sətir {i.row}
                      {i.column ? ` · ${i.column}` : ''}: {i.message}
                    </li>
                  ))}
                </ul>
              </>
            )}

            {preview.vatUnsetRows.length > 0 && !status && (
              <div className="form-actions">
                <p>
                  ƏDV seçilməyib: <strong>{preview.vatUnsetRows.length}</strong> sətir. Hamısına eyni rejim verin:
                </p>
                {VAT_CHOICES.map((c) => (
                  <button key={c.mode} type="button" className="btn-small" disabled={busy} onClick={() => void applyVat(c.mode)}>
                    {c.label}
                  </button>
                ))}
              </div>
            )}

            <h3>İdxala hazır sətirlər {preview.readyTotal > preview.ready.length && `(ilk ${preview.ready.length})`}</h3>
            <div className="product-list">
              {preview.ready.map((r) => (
                <div key={r.row} className="product-row">
                  <span className="product-name">{r.name}</span>
                  <span className="muted small">
                    {UNIT_LABEL[r.unit] ?? r.unit} · {SALE_TYPE_LABEL[r.saleType]} · {formatQty(r.openingQty)} · {formatMoney(r.salePrice)}
                  </span>
                  <span className="mono small">{r.barcode ?? '—'}</span>
                  <span className="tag">{r.vatMode ?? 'ƏDV seçilməyib'}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {preview && !status && (
          <section className="card card-wide">
            <h2>Təsdiq</h2>
            <label className="field">
              <span>Obyekt</span>
              <select value={outletId ?? ''} onChange={(e) => setOutletId(e.target.value || null)}>
                {outlets.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Anbar (başlanğıc qalıq bura yazılır)</span>
              <select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
                {warehouses.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                  </option>
                ))}
              </select>
            </label>
            {preview.vatUnsetRows.length > 0 && <p className="bad">Əvvəl bütün ƏDV seçimlərini edin.</p>}
            <button type="button" className="btn-primary" disabled={!canCommit} onClick={() => void commit()}>
              İdxalı təsdiqlə
            </button>
          </section>
        )}

        {status && (
          <section className="card card-wide">
            <h2>İdxal</h2>
            {running && (
              <>
                <p className="muted">Yazılır… {status.progress}%</p>
                <div className="progress" aria-label="Faiz">
                  <div style={{ width: `${status.progress}%` }} />
                </div>
              </>
            )}
            {status.status === 'failed' && <p className="bad">Xəta: {status.error}</p>}
            {status.status === 'done' && status.report && (
              <>
                <p className="ok">İdxal tamamlandı. KÖÇ sənədi: {status.report.docNumber}</p>
                <ul className="checks">
                  <li>
                    <span>Yaradılan məhsul</span>
                    <span>{status.report.created}</span>
                  </li>
                  <li>
                    <span>Başlanğıc qalıq sətirləri</span>
                    <span>{status.report.stockLines}</span>
                  </li>
                  <li>
                    <span>Maya qiymətsiz (0 hesablandı)</span>
                    <span>{status.report.costUnknown}</span>
                  </li>
                  <li>
                    <span>Bazada təkrar olduğu üçün rədd</span>
                    <span>{status.report.rejected.length}</span>
                  </li>
                </ul>
                {status.reversedLeg ? (
                  <p className="muted">Geri qaytarıldı: {status.reversedLeg}</p>
                ) : (
                  <button type="button" className="btn-small" disabled={busy} onClick={() => void reverse()}>
                    İdxalı geri qaytar
                  </button>
                )}
              </>
            )}
          </section>
        )}
      </main>
    </div>
  );
}
